import { Controller } from '@hotwired/stimulus'

// Drag-to-reorder for the chapter list. Plain HTML5 drag and drop (no extra npm
// dependency): each `item` target becomes draggable, and dragging one row over
// another reorders the <li>s within the <ol>. On drop we read the new id order
// straight from the DOM and PATCH it to `urlValue`; the server rewrites every
// chapter's position to be contiguous 1..N. The reorder is optimistic: the DOM
// is already in the new order by the time we send, so a 204 needs no follow-up.
// Auto-registered by the controllers glob.
export default class extends Controller {
  static targets = ['item']
  static values = { url: String }

  connect() {
    this.dragged = null
    this.onDragStart = this.onDragStart.bind(this)
    this.onDragOver = this.onDragOver.bind(this)
    this.onDrop = this.onDrop.bind(this)
    this.onDragEnd = this.onDragEnd.bind(this)

    this.itemTargets.forEach((item) => {
      item.setAttribute('draggable', 'true')
      item.addEventListener('dragstart', this.onDragStart)
      item.addEventListener('dragover', this.onDragOver)
      item.addEventListener('drop', this.onDrop)
      item.addEventListener('dragend', this.onDragEnd)
    })
  }

  disconnect() {
    this.itemTargets.forEach((item) => {
      item.removeEventListener('dragstart', this.onDragStart)
      item.removeEventListener('dragover', this.onDragOver)
      item.removeEventListener('drop', this.onDrop)
      item.removeEventListener('dragend', this.onDragEnd)
    })
  }

  onDragStart(event) {
    this.dragged = event.currentTarget
    event.dataTransfer.effectAllowed = 'move'
    // Some browsers require data to be set for the drag to start.
    event.dataTransfer.setData('text/plain', this.dragged.dataset.id || '')
    this.dragged.classList.add('opacity-50', 'bg-surface-2')
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const target = event.currentTarget
    if (!this.dragged || target === this.dragged) return

    // Insert the dragged row before or after the row it is hovering, based on
    // whether the cursor is in the top or bottom half of that row.
    const rect = target.getBoundingClientRect()
    const after = event.clientY - rect.top > rect.height / 2
    if (after) {
      target.parentNode.insertBefore(this.dragged, target.nextSibling)
    } else {
      target.parentNode.insertBefore(this.dragged, target)
    }
  }

  onDrop(event) {
    event.preventDefault()
  }

  onDragEnd() {
    if (this.dragged) {
      this.dragged.classList.remove('opacity-50', 'bg-surface-2')
    }
    this.dragged = null
    this.persist()
  }

  persist() {
    if (!this.hasUrlValue) return

    const newOrder = this.itemTargets.map((el) => el.dataset.id)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        Accept: 'application/json',
      },
      body: JSON.stringify({ ids: newOrder }),
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          // Don't throw: a failed save just means the visual order is ahead of
          // the server until the next reload. Surface it for debugging only.
          console.error('Chapter reorder failed:', response.status)
        }
      })
      .catch((error) => {
        console.error('Chapter reorder request failed:', error)
      })
  }
}
